import { Divider, Grid, Link } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useSnackbar } from "notistack"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { StandardLoadingButton } from "../../../shared/components/buttons/LoadingButton"
import { useApiMutation } from "../../../core/api/useApiMutation"
import { downloadGdprData } from "../profileApi"
import { useAppSelector } from "../../store/storeHooks"
import { InfoBox } from "./InfoBox"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons"
import { selectLoginUser } from "../../users/userSelectors"
import { MatchEnvironment } from "../../environment/components/MatchEnvironment"
import { EnvironmentType } from "../../environment/envTypes"
import { ComingSoonPlaceholder } from "../../../shared/components/placeholders/ComingSoonPlaceholder"

const GDPR_CORRECTION_URL = "https://prod.facilitynet.dk/support/gdpr/3a5fc506-7a94-4b17-8e97-362622b78236"

const useStyles = makeStyles()((theme) => ({
    container: {
        borderRadius: ".75rem",
        padding: "2rem",
        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.08)",
        border: "1px solid rgba(226, 232, 240, 1)",
        background: theme.palette.common.white,
        maxWidth: "44.375rem",
    },
    divider: {
        width: "100%",
    },
    correctionLink: {
        display: "inline-flex",
        alignItems: "center",
        gap: "0.625rem",
        fontSize: "1rem",
        color: "#2196F3",
    },
}))

export function GdprDownloadTab() {
    const { classes } = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const userDetails = useAppSelector(selectLoginUser)
    const { mutateAsync: fetchZip, isLoading } = useApiMutation(downloadGdprData, {})

    async function handleDownload() {
        if (!userDetails) return
        try {
            const blob = await fetchZip({ userId: userDetails.id })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement("a")
            link.href = url
            link.download = "my-data.zip"
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch {
            enqueueSnackbar({ variant: "error", message: "Failed to download data. Please try again." })
        }
    }

    function buildCorrectionUrl() {
        if (!userDetails) return undefined
        const params = new URLSearchParams({ userId: String(userDetails.id), userEmail: userDetails?.email })
        return `${GDPR_CORRECTION_URL}?${params.toString()}`
    }

    return (
        <>
            <MatchEnvironment environments={[EnvironmentType.Demo, EnvironmentType.QA, EnvironmentType.Prod]}>
                <LocalizedStrict id="planning-tabs-placeholder" attrs={{ title: true, body: true }}>
                    <ComingSoonPlaceholder title="Coming soon" body="We are working to bring you this feature." />
                </LocalizedStrict>
            </MatchEnvironment>
            <MatchEnvironment environments={[EnvironmentType.Local, EnvironmentType.Test]}>
                <Grid container direction="column" gap="1.5rem" alignItems="flex-start" className={classes.container}>
                    <LocalizedStrict id="profile-gdpr-info-box" attrs={{ description: true }}>
                        <InfoBox description="Here you can download a copy of your own personal data we have registered in Facilitynet." />
                    </LocalizedStrict>

                    <LocalizedStrict id="profile-gdpr-export-button">
                        <StandardLoadingButton loading={isLoading} onClick={handleDownload} fullWidth={false}>
                            Export my data
                        </StandardLoadingButton>
                    </LocalizedStrict>

                    <Divider className={classes.divider} />

                    <LocalizedStrict id="profile-gdpr-correction-info-box" attrs={{ description: true }}>
                        <InfoBox description="Here you can request correction of your personal data." />
                    </LocalizedStrict>

                    <Link
                        href={buildCorrectionUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="none"
                        className={classes.correctionLink}
                    >
                        <LocalizedStrict id="profile-gdpr-correction-button">
                            <span>Request correction of my data</span>
                        </LocalizedStrict>
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ width: "1.375rem", height: "1rem" }} />
                    </Link>
                </Grid>
            </MatchEnvironment>
        </>
    )
}
